import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LOGO } from "@/lib/brand";
import ServiceRequestDialog from "@/components/ServiceRequestDialog";
import {
  ArrowRight, CheckCircle2, Shield, Link2, Menu, X,
} from "lucide-react";
import { useState, useEffect, lazy, Suspense } from "react";
import { RubikCube } from "@/components/RubikCube";
import { AmbientLights } from "@/components/AmbientLights";
import { CodeExperience } from "@/components/home/CodeExperience";
import { InvisibleInfrastructure, ConnectedServices, ProcessDiagram, PlatformPreview, LongTerm } from "@/components/home/Sections";
import { Reveal } from "@/components/home/Reveal";

const CrystalCube = lazy(() => import("@/components/CrystalCube"));

function hasWebGL() {
  try {
    const c = document.createElement("canvas");
    return !!(window.WebGLRenderingContext && (c.getContext("webgl") || c.getContext("experimental-webgl")));
  } catch { return false; }
}

/* Approved ECOREXS brand lockup: emerald crystal cube icon + wordmark (transparent assets) */
function BrandLockup({ size = "header" }) {
  const w = size === "footer" ? "w-[200px] sm:w-[270px]" : "w-[150px] sm:w-[200px]";
  return (
    <div className="flex items-center shrink-0" data-testid="brand-logo">
      <img src={LOGO.wordmark} alt="ECOREXS" className={`${w} h-auto object-contain`} />
    </div>
  );
}

/* Animated hero symbol — the ECOREXS emerald crystal cube, alive but subtle */
function BrandSymbol({ className = "" }) {
  return (
    <div className={`relative ${className}`} data-testid="hero-brand-symbol" style={{ perspective: "1100px" }}>
      {/* pulsing emerald glow */}
      <div className="absolute inset-[6%] -z-10 rounded-full bg-[#67F2B3]/25 blur-3xl animate-breathe" />
      {/* slow rotating ambient halo */}
      <div
        className="absolute inset-[-12%] -z-10 rounded-full animate-spin-slow"
        style={{ background: "conic-gradient(from 0deg, transparent 0%, rgba(103,242,179,0.18) 25%, transparent 55%, rgba(103,242,179,0.1) 80%, transparent 100%)", filter: "blur(28px)" }}
      />
      <RubikCube className="relative w-full h-full" />
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [enable3D, setEnable3D] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const wide = window.matchMedia("(min-width: 768px)").matches;
    setEnable3D(wide && !reduce && hasWebGL());
  }, []);

  return (
    <div className="relative isolate min-h-screen overflow-x-hidden text-[#F4FFF9]"
      style={{ background: "radial-gradient(120% 70% at 50% 0%, #0B2E22 0%, #071C15 38%, #050B09 70%)" }}>
      <AmbientLights />

      {/* Ambient drifting lights */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-32 -left-24 h-[480px] w-[480px] rounded-full bg-[#67F2B3]/12 blur-[120px] animate-drift" />
        <div className="absolute top-1/3 -right-24 h-[420px] w-[420px] rounded-full bg-[#0D3B2D]/60 blur-[120px] animate-drift" style={{ animationDelay: "3s" }} />
        <div className="absolute bottom-0 left-1/3 h-[360px] w-[360px] rounded-full bg-[#67F2B3]/8 blur-[120px] animate-drift" style={{ animationDelay: "6s" }} />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#050B09]/70 border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-5 h-16 sm:h-20 flex items-center justify-between">
          <BrandLockup />
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-[#F4FFF9]/80">
            <a href="#home" className="hover:text-[#67F2B3] transition-colors" data-testid="nav-home">Home</a>
            <a href="#services" className="hover:text-[#67F2B3] transition-colors" data-testid="nav-services">Services</a>
            <a href="#support" className="hover:text-[#67F2B3] transition-colors" data-testid="nav-support">Support</a>
            <button onClick={() => setReqOpen(true)} data-testid="nav-request-services" className="hover:text-[#67F2B3] transition-colors font-medium">Request Services</button>
            <Button onClick={() => navigate("/login")} data-testid="nav-client-login-button"
              className="bg-[#67F2B3] hover:bg-[#8affca] text-[#071A13] font-semibold rounded-lg transition-all hover:shadow-[0_0_24px_rgba(103,242,179,0.35)]">
              Client Login
            </Button>
          </nav>
          <button className="md:hidden text-[#F4FFF9]" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-button">
            {menuOpen ? <X /> : <Menu />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-white/10 bg-[#071A13] px-5 py-4 flex flex-col gap-3 text-sm font-medium text-[#F4FFF9]/85">
            <a href="#services" onClick={() => setMenuOpen(false)} data-testid="m-nav-services">Services</a>
            <a href="#support" onClick={() => setMenuOpen(false)} data-testid="m-nav-support">Support</a>
            <button onClick={() => { setMenuOpen(false); setReqOpen(true); }} data-testid="m-nav-request-services" className="text-left">Request Services</button>
            <Button onClick={() => navigate("/login")} data-testid="m-nav-client-login" className="bg-[#67F2B3] text-[#071A13] font-semibold rounded-lg w-full">Client Login</Button>
          </div>
        )}
      </header>

      {/* Hero */}
      <section id="home" className="relative">
        <div data-constellation-exclude className="max-w-6xl mx-auto px-5 pt-16 pb-20 md:pt-28 md:pb-32 grid md:grid-cols-2 gap-10 md:gap-8 items-center">
          {/* Mobile symbol on top (centered), hidden on desktop */}
          <div className="md:hidden flex justify-center mb-2 animate-fade-up">
            <BrandSymbol className="w-56 h-56" />
          </div>

          <div className="text-center md:text-left animate-fade-up" style={{ animationDelay: "0.05s" }}>
            <div className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#67F2B3] bg-[#67F2B3]/10 border border-[#67F2B3]/25 rounded-full px-3 py-1 mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-[#67F2B3]" /> Managed Digital Infrastructure
            </div>
            <h1 className="font-heading font-semibold text-5xl sm:text-6xl lg:text-7xl tracking-[-0.03em] leading-[0.98]">
              Digital Systems.<br />
              <span className="bg-gradient-to-r from-[#67F2B3] via-[#A8F5CF] to-[#67F2B3] bg-clip-text text-transparent">Simplified.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[#F4FFF9]/70 max-w-md mx-auto md:mx-0 leading-relaxed">
              Reliable digital systems, managed services and technology that keeps your business moving.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center md:justify-start">
              <Button onClick={() => document.getElementById("services").scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-services-button"
                className="bg-[#67F2B3] hover:bg-[#8affca] text-[#071A13] font-semibold rounded-lg px-6 py-6 transition-all hover:shadow-[0_0_28px_rgba(103,242,179,0.4)]">
                Explore Services <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
              <Button onClick={() => navigate("/login")} variant="outline" data-testid="hero-portal-button"
                className="border-[#67F2B3]/30 bg-transparent text-[#F4FFF9] hover:bg-[#67F2B3]/10 hover:border-[#67F2B3]/60 rounded-lg px-6 py-6 transition-all">
                Client Portal
              </Button>
            </div>
          </div>

          {/* Desktop symbol — 3D crystal cube with graceful PNG fallback */}
          <div className="hidden md:flex justify-center animate-fade-up" style={{ animationDelay: "0.15s" }}>
            {enable3D ? (
              <Suspense fallback={<BrandSymbol className="w-[380px] h-[380px] lg:w-[440px] lg:h-[440px]" />}>
                <CrystalCube className="w-[420px] h-[420px] lg:w-[480px] lg:h-[480px]" />
              </Suspense>
            ) : (
              <BrandSymbol className="w-[380px] h-[380px] lg:w-[440px] lg:h-[440px]" />
            )}
          </div>
        </div>
      </section>

      <InvisibleInfrastructure />
      <CodeExperience />
      <ConnectedServices onRequest={() => setReqOpen(true)} />
      <ProcessDiagram />
      <PlatformPreview />
      <LongTerm />

      {/* Why ECOREXS */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-5 py-20 md:py-28 grid sm:grid-cols-3 gap-10">
          {[
            { icon: CheckCircle2, t: "Simple", d: "Clear services and transparent billing, with no unnecessary complexity." },
            { icon: Shield, t: "Reliable", d: "Dependable systems and support your business can count on." },
            { icon: Link2, t: "Connected", d: "Integrated tools and automated workflows that just work together." },
          ].map((v) => (
            <Reveal key={v.t} className="text-center sm:text-left">
              <div className="inline-flex h-11 w-11 rounded-lg bg-[#67F2B3]/12 border border-[#67F2B3]/25 items-center justify-center mx-auto sm:mx-0">
                <v.icon className="h-5 w-5 text-[#67F2B3]" />
              </div>
              <h3 className="font-heading font-semibold text-xl mt-4">{v.t}</h3>
              <p className="text-sm text-[#F4FFF9]/60 mt-2">{v.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Support callout */}
      <section id="support" className="max-w-6xl mx-auto px-5 pb-24 md:pb-32">
        <Reveal className="relative overflow-hidden rounded-2xl p-8 sm:p-14 border border-[#67F2B3]/15"
          style={{ background: "linear-gradient(135deg, #0D3B2D 0%, #071A13 60%, #030B08 100%)" }}>
          <div className="relative z-10 max-w-xl">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl">
              Dependable support, <span className="text-[#67F2B3]">whenever you need it.</span>
            </h2>
            <p className="text-[#F4FFF9]/70 mt-4 text-sm sm:text-base">
              Your services, subscriptions and invoices — all managed securely and professionally.
              Reach us anytime through your client portal.
            </p>
            <Button onClick={() => navigate("/login")} data-testid="callout-portal-button"
              className="mt-6 bg-[#67F2B3] hover:bg-[#8affca] text-[#071A13] font-semibold rounded-lg px-6 py-6 transition-all hover:shadow-[0_0_28px_rgba(103,242,179,0.4)]">
              Go to Client Portal
            </Button>
          </div>
          <div className="absolute right-0 top-0 h-full w-2/3 opacity-30 animate-breathe"
            style={{ background: "radial-gradient(circle at 75% 40%, #67F2B3, transparent 60%)" }} />
        </Reveal>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/[0.06] bg-[#050B09]/80">
        <div className="max-w-6xl mx-auto px-5 py-14 grid sm:grid-cols-2 md:grid-cols-4 gap-8">
          <div className="sm:col-span-2 md:col-span-1">
            <BrandLockup size="footer" />
            <p className="text-sm text-[#F4FFF9]/50 mt-4">ecorexs.com</p>
            <div className="inline-flex items-center gap-2 mt-4 text-xs text-[#67F2B3]">
              <span className="h-2 w-2 rounded-full bg-[#67F2B3] animate-pulse" /> All systems operational
            </div>
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#67F2B3] mb-3">Company</div>
            <ul className="space-y-2 text-sm text-[#F4FFF9]/60">
              <li><button onClick={() => navigate("/login")} data-testid="footer-login" className="hover:text-[#67F2B3] transition-colors">Client Login</button></li>
              <li><a href="#support" className="hover:text-[#67F2B3] transition-colors">Support</a></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#67F2B3] mb-3">Legal</div>
            <ul className="space-y-2 text-sm text-[#F4FFF9]/60">
              <li><button onClick={() => navigate("/legal/privacy")} data-testid="footer-privacy" className="hover:text-[#67F2B3] transition-colors">Privacy Policy</button></li>
              <li><button onClick={() => navigate("/legal/terms")} data-testid="footer-terms" className="hover:text-[#67F2B3] transition-colors">Terms of Service</button></li>
              <li><button onClick={() => navigate("/legal/billing")} data-testid="footer-billing" className="hover:text-[#67F2B3] transition-colors">Billing & Cancellation</button></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#67F2B3] mb-3">Contact</div>
            <p className="text-sm text-[#F4FFF9]/60">For account and billing questions, sign in to your client portal.</p>
          </div>
        </div>
        <div className="border-t border-white/5">
          <div className="max-w-6xl mx-auto px-5 py-5 text-xs text-[#F4FFF9]/40 flex flex-col sm:flex-row justify-between items-center gap-2">
            <span>© {new Date().getFullYear()} ECOREXS. All rights reserved. · <a href="mailto:support@ecorexs.com" data-testid="footer-support-email" className="hover:text-[#67F2B3] transition-colors">support@ecorexs.com</a></span>
            <div className="flex items-center gap-4">
              <span>Managed Digital Infrastructure</span>
              <button onClick={() => navigate("/admin-login")} data-testid="footer-admin-link"
                className="text-[#F4FFF9]/30 hover:text-[#67F2B3]/80 transition-colors">Admin</button>
            </div>
          </div>
        </div>
      </footer>
      <ServiceRequestDialog open={reqOpen} onOpenChange={setReqOpen} />
    </div>
  );
}
